import {
  Alert,
  AlertDescription,
  HStack,
  VisuallyHidden,
} from "@chakra-ui/react";
import { AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Outlet, useNavigate } from "react-router";

import { Restricted } from "@/app/components/Restricted";
import HeaderLayout from "@/common/components/HeaderLayout";
import { MotionScaleFade } from "@/common/components/MotionComponents";
import { UpsertPopover } from "@/common/components/UpsertPopover";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useTableDisplayModeParam } from "@/common/hooks/useTableDisplayMode";
import { FeatureViewSelect } from "@/feature/components/FeatureViewSelect";
import { FlagsTable } from "@/feature/components/FlagsTable";
import { StaleFlagsTable } from "@/feature/components/StaleFlagsTable";
import { useFlags } from "@/feature/data/useFlags";
import { useFlagViewUpsertMutations } from "@/feature/data/useFlagViewUpsertMutations";
import { useFlagsTableState } from "@/feature/hooks/useFlagsTableState";
import {
  getFlagListStatus,
  getFlagViewDisplayName,
} from "@/feature/utils/flagView";
import { useFlag } from "@reflag/react-sdk";
import { FlagViewDTO } from "@reflag/shared/flagViewAPI";
import { FeatureViewUrl } from "@reflag/shared/urls";

export function Flags({ view }: { view: FlagViewDTO }) {
  const { env, appId, userId } = useEnvironment();
  const navigate = useNavigate();
  const { isEnabled: searchArchivedFlagsEnabled } = useFlag(
    "search-archived-flags",
  );

  const {
    isDirty,
    searchQuery,
    sorting,
    onSortingChange,
    onColumnStatesChange,
  } = useFlagsTableState(view);

  // Get display mode for tree view support
  const [displayMode] = useTableDisplayModeParam();

  // We only handle sorting one column for now
  const { id: sortBy, desc: sortDesc } = sorting[0] ?? {};

  const sortOrder = sortDesc === true ? "desc" : "asc";

  // Archived flags are included when searching views that normally show only
  // active flags. Explicit Archived and Stale views remain scoped.
  const status = getFlagListStatus(
    view.type,
    searchQuery,
    searchArchivedFlagsEnabled,
  );
  const owner = view.type === "my" ? userId : undefined;
  const displayName = getFlagViewDisplayName(view);

  const { data, isLoading, isFetching, isError, refetch } = useFlags({
    appId,
    envId: env.id,
    sortBy,
    sortOrder,
    status,
    owner,
    view: view.type === "custom" ? view.id : undefined,
    sortType: displayMode === "tree" ? "hierarchical" : "flat",
  });

  const flagViewUpsert = useFlagViewUpsertMutations(
    view,
    data?.data?.map((f) => f.id) || [],
    {
      onSuccess: ({ id }, { action }) => {
        if (action == "create") navigate(FeatureViewUrl(env, id));
      },
    },
  );

  return (
    <>
      <VisuallyHidden>
        <h1>{displayName}</h1>
      </VisuallyHidden>
      <HeaderLayout
        actions={
          <HStack spacing={4}>
            <AnimatePresence>
              {isDirty && (
                <MotionScaleFade origin="bottom">
                  <Restricted role="contributor">
                    <UpsertPopover
                      canUpdate={!isLoading && !isError}
                      entity="View"
                      mutation={flagViewUpsert}
                      onReset={() => {
                        onSortingChange([]);
                        onColumnStatesChange([]);
                      }}
                    >
                      {isError && (
                        <Alert status="warning">
                          <AlertDescription>
                            Creation of a new view is disabled because flags
                            could not be loaded.
                          </AlertDescription>
                        </Alert>
                      )}
                    </UpsertPopover>
                  </Restricted>
                </MotionScaleFade>
              )}
            </AnimatePresence>
          </HStack>
        }
        title={
          <HStack ml={-2} spacing={1}>
            <FeatureViewSelect />
          </HStack>
        }
        borderless
      >
        <Helmet>
          <title>{`Flags › ${displayName}`}</title>
        </Helmet>
        {view.type === "stale" ? (
          <StaleFlagsTable
            data={
              data?.data
                ? {
                    data: data.data,
                    metadata: {
                      viewId: data.metadata?.viewId ?? view.id,
                      sortType: data.metadata?.sortType ?? "flat",
                    },
                  }
                : undefined
            }
            isError={isError}
            isFetching={isFetching}
            isLoading={isLoading}
            view={view}
            onRetry={refetch}
          />
        ) : (
          <FlagsTable
            data={
              data?.data
                ? {
                    data: data.data,
                    metadata: {
                      viewId: data.metadata?.viewId ?? view.id,
                      sortType: data.metadata?.sortType ?? "flat",
                    },
                  }
                : undefined
            }
            isError={isError}
            isFetching={isFetching}
            isLoading={isLoading}
            view={view}
            onRetry={refetch}
          />
        )}
        <Outlet />
      </HeaderLayout>
    </>
  );
}
